import { useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '../components/ui/chart';
import { Bar, BarChart, XAxis, YAxis, CartesianGrid } from 'recharts';
import html2pdf from 'html2pdf.js';
import { Download, Edit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const timeOfDayChartConfig: ChartConfig = {
  sales: {
    label: 'Sales %',
    color: '#475569',
  },
};

const revenueChartConfig: ChartConfig = {
  initialBox: {
    label: 'Initial Box Revenue',
    color: '#475569',
  },
  subscription: {
    label: 'Subscription Revenue',
    color: '#64748b',
  },
};

export default function LushReportPage() {
  const navigate = useNavigate();
  const reportContentRef = useRef<HTMLDivElement>(null);

  // Load report data from localStorage or use defaults
  const loadReportData = () => {
    try {
      const saved = localStorage.getItem('lush-report-data');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to load report data:', e);
    }
    return null;
  };

  const reportData = loadReportData();
  const data = reportData || {
    location: 'Coal Drops Yard, King\'s Cross, London',
    duration: '6 Months',
    product: '£120 Lush Experience Box',
    heroImages: [],
    footfallMonthly: 620000,
    footfallTotal: 3720000,
    screenImpressionsMonthly: 1850000,
    screenImpressionsTotal: 11100000,
    adPlayouts: '6 ads rotating',
    interactionsMonthly: 21700,
    interactionsTotal: 130200,
    browsingSessionsMonthly: 16900,
    browsingSessionsTotal: 101400,
    averageBrowseTime: '52 seconds',
    footfallToBrowseRate: '2.7%',
    conversionFootfallToPurchase: '0.42%',
    conversionBrowsingToPurchase: '15.4%',
    averageSellingPrice: '£120',
    monthlyUnitsSold: 2604,
    totalUnitsSold: 15624,
    initialBoxRevenueMonthly: '£312,480',
    initialBoxRevenueTotal: '£1,874,880',
    subscriptionRevenueMonthly: '£174,000',
    subscriptionRevenueTotal: '£696,000',
    totalCampaignRevenueMonthly: '£486,480',
    totalCampaignRevenueTotal: '£2,570,880',
    totalTransactionValue: '£2.57M',
    threeMonthCompletionRate: '81%',
    churnRate: '19%',
    autoRenewTakeUp: '64% of retained base',
    averageLTV: '£186',
    footfallUplift: '+11% vs prior 6-month baseline',
    giftCardRedemption: '23% of machine buyers',
    sameDayCrossPurchase: '18%',
    incrementalStoreRevenue: '£420,000',
    sales10am1pm: 14,
    sales1pm4pm: 26,
    sales4pm7pm: 41,
    sales7pm9pm: 19,
    overallExperienceScore: '4.7 / 5',
    easeOfPurchaseScore: '4.8 / 5',
    giftAppealRating: '4.9 / 5',
    wouldRecommend: '92%',
    feedbackTheme1: 'Best last-minute premium gift solution.',
    feedbackTheme2: 'Loved choosing scent in Month 2.',
    feedbackTheme3: 'More exciting than ordering online.',
    strategicTakeaway1: 'Premium £120 experiential product performs strongly in high-dwell retail environment.',
    strategicTakeaway2: 'Strong subscription retention (81% completion rate).',
    strategicTakeaway3: 'Machine drove measurable store halo effect (+11% uplift).',
    strategicTakeaway4: '15%+ browse-to-purchase conversion.',
    strategicTakeaway5: '£2.57M revenue generated from a single unit over 6 months.',
    strategicTakeaway6: 'Proven model for experiential retail + subscription hybrid commerce.',
  };

  // Chart data
  const timeOfDayData = [
    { name: '10am-1pm', sales: data.sales10am1pm },
    { name: '1pm-4pm', sales: data.sales1pm4pm },
    { name: '4pm-7pm', sales: data.sales4pm7pm },
    { name: '7pm-9pm', sales: data.sales7pm9pm },
  ];

  // Calculate revenue data from saved values
  const initialBoxMonthly = parseFloat(data.initialBoxRevenueMonthly?.replace(/[£,]/g, '') || '312480');
  const subscriptionMonthly = parseFloat(data.subscriptionRevenueMonthly?.replace(/[£,]/g, '') || '174000');
  const revenueData = [
    { month: 'Month 1', initialBox: initialBoxMonthly, subscription: subscriptionMonthly },
    { month: 'Month 2', initialBox: initialBoxMonthly, subscription: subscriptionMonthly },
    { month: 'Month 3', initialBox: initialBoxMonthly, subscription: subscriptionMonthly },
    { month: 'Month 4', initialBox: initialBoxMonthly, subscription: subscriptionMonthly },
    { month: 'Month 5', initialBox: initialBoxMonthly, subscription: subscriptionMonthly },
    { month: 'Month 6', initialBox: initialBoxMonthly, subscription: subscriptionMonthly },
  ];

  async function handleExportPDF() {
    if (!reportContentRef.current) {
      alert('Report content not found. Please try again.');
      return;
    }
    
    try {
      const headerElement = document.getElementById('lush-report-header');
      const originalDisplay = headerElement?.style.display || '';
      if (headerElement) {
        headerElement.style.display = 'none';
      }
      
      const element = reportContentRef.current;
      const opt = {
        margin: [10, 10, 10, 10] as [number, number, number, number],
        filename: `Lush-Experience-Box-Performance-Report-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          letterRendering: true,
          backgroundColor: '#ffffff',
        },
        jsPDF: { 
          unit: 'mm' as const, 
          format: 'a4' as const, 
          orientation: 'portrait' as const,
          compress: true,
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] as any }
      };
      
      await html2pdf().set(opt).from(element).save();
      
      if (headerElement) {
        headerElement.style.display = originalDisplay;
      }
    } catch (error: any) {
      console.error('Failed to export PDF:', error);
      alert(`Failed to export PDF: ${error.message || 'Unknown error'}`);
      
      const headerElement = document.getElementById('lush-report-header');
      if (headerElement) {
        headerElement.style.display = '';
      }
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto">
        {/* Minimal Header */}
        <div id="lush-report-header" className="sticky top-0 bg-white border-b border-slate-200 z-10 px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-light tracking-tight text-slate-900">Lush Experience Box</h1>
            <p className="text-xs text-slate-500 mt-0.5">Performance Report • 6-Month Campaign</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => navigate('/reports/lush-experience-box/edit')}
              variant="outline" 
              size="sm"
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              <Edit className="h-3.5 w-3.5 mr-2" />
              Edit
            </Button>
            <Button 
              onClick={handleExportPDF} 
              variant="outline" 
              size="sm"
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              <Download className="h-3.5 w-3.5 mr-2" />
              Download PDF
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div ref={reportContentRef} className="bg-white px-8 py-12">
          {/* Cover Section */}
          <div className="mb-16 text-center">
            <div className="inline-block mb-6">
              <span className="text-6xl">🌿</span>
            </div>
            <h1 className="text-5xl font-light tracking-tight text-slate-900 mb-4">
              Lush Experience Box
            </h1>
            <p className="text-xl text-slate-600 font-light mb-8">
              Performance Report
            </p>
            <div className="flex justify-center gap-8 text-sm text-slate-500">
              <span>{data.location}</span>
              <span>•</span>
              <span>{data.duration}</span>
              <span>•</span>
              <span>{data.product}</span>
            </div>
          </div>

          <Separator className="my-12" />

          {/* Hero Images */}
          {data.heroImages && data.heroImages.length > 0 && (
            <div className="mb-16">
              <div className={`grid gap-6 ${data.heroImages.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                {data.heroImages.map((img: string, idx: number) => (
                  <div key={idx} className="overflow-hidden rounded-sm border border-slate-200">
                    <img
                      src={img}
                      alt={`Hero ${idx + 1}`}
                      className="w-full h-auto object-cover"
                      style={{ maxHeight: '500px' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23f1f5f9"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%2394a3b8"%3EImage%3C/text%3E%3C/svg%3E';
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <Separator className="my-12" />

          {/* Executive Summary */}
          <div className="mb-16">
            <h2 className="text-2xl font-light text-slate-900 mb-8">Executive Summary</h2>
            <div className="grid grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-5xl font-light text-slate-900 mb-2">{data.totalCampaignRevenueTotal || '£2.57M'}</div>
                <div className="text-sm text-slate-600 uppercase tracking-wide">Total Revenue</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-light text-slate-900 mb-2">{data.conversionBrowsingToPurchase || '15.4%'}</div>
                <div className="text-sm text-slate-600 uppercase tracking-wide">Browse-to-Purchase</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-light text-slate-900 mb-2">{data.threeMonthCompletionRate || '81%'}</div>
                <div className="text-sm text-slate-600 uppercase tracking-wide">3-Month Retention</div>
              </div>
            </div>
          </div>

          <Separator className="my-12" />

          {/* Campaign Overview */}
          <div className="mb-16">
            <h2 className="text-2xl font-light text-slate-900 mb-6">Campaign Overview</h2>
            <div className="grid grid-cols-2 gap-12">
              <div>
                <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-4">Product Structure</h3>
                <ul className="space-y-3 text-slate-700">
                  <li className="flex items-start">
                    <span className="text-slate-400 mr-3">•</span>
                    <span>Month 1: Physical premium gift box dispensed from machine</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-slate-400 mr-3">•</span>
                    <span>Optional digital or printed gift card</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-slate-400 mr-3">•</span>
                    <span>Months 2-3: Subscription with custom scent selection</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-slate-400 mr-3">•</span>
                    <span>Auto-renew from Month 4</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-4">Key Metrics</h3>
                <div className="space-y-4">
                  <div className="flex justify-between border-b border-slate-100 pb-3">
                    <span className="text-slate-600">Footfall (6 months)</span>
                    <span className="font-medium text-slate-900">{(data.footfallTotal / 1000000).toFixed(2)}M</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-100 pb-3">
                    <span className="text-slate-600">Screen Impressions</span>
                    <span className="font-medium text-slate-900">{(data.screenImpressionsTotal / 1000000).toFixed(1)}M</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-100 pb-3">
                    <span className="text-slate-600">Total Units Sold</span>
                    <span className="font-medium text-slate-900">{data.totalUnitsSold.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-100 pb-3">
                    <span className="text-slate-600">Average LTV</span>
                    <span className="font-medium text-slate-900">{data.averageLTV}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Separator className="my-12" />

          {/* Performance Metrics */}
          <div className="mb-16">
            <h2 className="text-2xl font-light text-slate-900 mb-8">Performance Metrics</h2>
            
            {/* Footfall & Visibility */}
            <div className="mb-12">
              <h3 className="text-lg font-normal text-slate-700 mb-6">Footfall & Visibility</h3>
              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium text-slate-700">Metric</TableHead>
                      <TableHead className="text-right font-medium text-slate-700">Monthly Average</TableHead>
                      <TableHead className="text-right font-medium text-slate-700">6-Month Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="text-slate-600">Footfall (zone)</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.footfallMonthly.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.footfallTotal.toLocaleString()}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Screen Impressions</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.screenImpressionsMonthly.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.screenImpressionsTotal.toLocaleString()}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Ad Playouts</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.adPlayouts}</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">~780,000 total plays</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Engagement Metrics */}
            <div className="mb-12">
              <h3 className="text-lg font-normal text-slate-700 mb-6">Engagement Metrics</h3>
              <div className="border border-slate-200 rounded-sm overflow-hidden mb-4">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium text-slate-700">Metric</TableHead>
                      <TableHead className="text-right font-medium text-slate-700">Monthly Avg</TableHead>
                      <TableHead className="text-right font-medium text-slate-700">6-Month Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="text-slate-600">Screen Clicks / Interactions</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.interactionsMonthly.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.interactionsTotal.toLocaleString()}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Browsing Sessions</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.browsingSessionsMonthly.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.browsingSessionsTotal.toLocaleString()}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Average Browse Time</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.averageBrowseTime}</TableCell>
                      <TableCell className="text-right text-slate-400">—</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              <p className="text-sm text-slate-600">
                <span className="font-medium">Footfall → Browse Rate:</span> {data.footfallToBrowseRate}
              </p>
            </div>

            {/* Conversion Performance */}
            <div className="mb-12">
              <h3 className="text-lg font-normal text-slate-700 mb-6">Conversion Performance</h3>
              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium text-slate-700">Metric</TableHead>
                      <TableHead className="text-right font-medium text-slate-700">Result</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="text-slate-600">Conversion: Footfall → Purchase</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.conversionFootfallToPurchase}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Conversion: Browsing → Purchase</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.conversionBrowsingToPurchase}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Average Selling Price</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.averageSellingPrice}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Monthly Units Sold</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.monthlyUnitsSold.toLocaleString()}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Total Units (6 months)</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.totalUnitsSold.toLocaleString()}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Revenue Performance */}
            <div className="mb-12">
              <h3 className="text-lg font-normal text-slate-700 mb-6">Revenue Performance</h3>
              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium text-slate-700">Metric</TableHead>
                      <TableHead className="text-right font-medium text-slate-700">Monthly</TableHead>
                      <TableHead className="text-right font-medium text-slate-700">6-Month Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="text-slate-600">Initial Box Revenue</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.initialBoxRevenueMonthly}</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.initialBoxRevenueTotal}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Subscription Revenue (blended)</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.subscriptionRevenueMonthly}</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.subscriptionRevenueTotal}</TableCell>
                    </TableRow>
                    <TableRow className="bg-slate-50">
                      <TableCell className="font-semibold text-slate-900">Total Campaign Revenue</TableCell>
                      <TableCell className="text-right font-semibold text-slate-900">{data.totalCampaignRevenueMonthly}</TableCell>
                      <TableCell className="text-right font-semibold text-slate-900 text-lg">{data.totalCampaignRevenueTotal}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Total Transaction Value Processed</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.totalTransactionValue}</TableCell>
                      <TableCell className="text-right text-slate-400">—</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              
              {/* Revenue Chart */}
              <div className="mt-8 border border-slate-200 rounded-sm p-6 bg-slate-50/50">
                <h4 className="text-sm font-medium text-slate-700 mb-4">Revenue by Month</h4>
                <ChartContainer config={revenueChartConfig} className="h-[320px]">
                  <BarChart data={revenueData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fontSize: 11, fill: '#475569' }}
                      axisLine={{ stroke: '#cbd5e1' }}
                    />
                    <YAxis 
                      tick={{ fontSize: 11, fill: '#475569' }}
                      axisLine={{ stroke: '#cbd5e1' }}
                      tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                    />
                    <ChartTooltip 
                      content={<ChartTooltipContent formatter={(value: any) => `£${(Array.isArray(value) ? value[0] : value).toLocaleString('en-GB')}`} />}
                    />
                    <Bar dataKey="initialBox" stackId="revenue" fill="#475569" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="subscription" stackId="revenue" fill="#64748b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ChartContainer>
              </div>
            </div>

            {/* Retention & Churn */}
            <div className="mb-12">
              <h3 className="text-lg font-normal text-slate-700 mb-6">Retention & Churn</h3>
              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium text-slate-700">Metric</TableHead>
                      <TableHead className="text-right font-medium text-slate-700">Result</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="text-slate-600">3-Month Completion Rate</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.threeMonthCompletionRate}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Churn Rate</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.churnRate}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Auto-Renew Take-Up (Month 4+)</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.autoRenewTakeUp}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Average Lifetime Value per Customer</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.averageLTV}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Store Halo Effect */}
            <div className="mb-12">
              <h3 className="text-lg font-normal text-slate-700 mb-6">Store Halo Effect (King's Cross Store)</h3>
              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium text-slate-700">Metric</TableHead>
                      <TableHead className="text-right font-medium text-slate-700">Impact</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="text-slate-600">Footfall Uplift</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.footfallUplift}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Gift Card Redemption In-Store</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.giftCardRedemption}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Same-Day Cross Purchase</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.sameDayCrossPurchase}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Estimated Incremental Store Revenue</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.incrementalStoreRevenue}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Sales by Time of Day */}
            <div className="mb-12">
              <h3 className="text-lg font-normal text-slate-700 mb-6">Sales by Time of Day</h3>
              <div className="border border-slate-200 rounded-sm overflow-hidden mb-4">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium text-slate-700">Time</TableHead>
                      <TableHead className="text-right font-medium text-slate-700">% of Sales</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="text-slate-600">10am - 1pm</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.sales10am1pm}%</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">1pm - 4pm</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.sales1pm4pm}%</TableCell>
                    </TableRow>
                    <TableRow className="bg-slate-50">
                      <TableCell className="font-medium text-slate-900">4pm - 7pm</TableCell>
                      <TableCell className="text-right font-semibold text-slate-900">{data.sales4pm7pm}%</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">7pm - 9pm</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.sales7pm9pm}%</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              <p className="text-sm text-slate-600 mb-6">
                <span className="font-medium">Peak conversion window:</span> Early evening (4pm - 7pm)
              </p>
              
              {/* Time of Day Chart */}
              <div className="mt-8 border border-slate-200 rounded-sm p-6 bg-slate-50/50">
                <h4 className="text-sm font-medium text-slate-700 mb-4">Sales Distribution by Time of Day</h4>
                <ChartContainer config={timeOfDayChartConfig} className="h-[280px]">
                  <BarChart data={timeOfDayData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fontSize: 11, fill: '#475569' }}
                      axisLine={{ stroke: '#cbd5e1' }}
                    />
                    <YAxis 
                      tick={{ fontSize: 11, fill: '#475569' }}
                      axisLine={{ stroke: '#cbd5e1' }}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <ChartTooltip 
                      content={<ChartTooltipContent formatter={(value: any) => `${Array.isArray(value) ? value[0] : value}%`} />}
                    />
                    <Bar dataKey="sales" fill="#475569" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ChartContainer>
              </div>
            </div>

            {/* Customer Feedback */}
            <div>
              <h3 className="text-lg font-normal text-slate-700 mb-6">Customer Feedback</h3>
              <div className="border border-slate-200 rounded-sm overflow-hidden mb-6">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium text-slate-700">Metric</TableHead>
                      <TableHead className="text-right font-medium text-slate-700">Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="text-slate-600">Overall Experience Score</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.overallExperienceScore}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Ease of Purchase</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.easeOfPurchaseScore}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Gift Appeal Rating</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.giftAppealRating}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-slate-600">Would Recommend</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">{data.wouldRecommend}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3">Common Feedback Themes</h4>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-start">
                    <span className="text-slate-400 mr-3">•</span>
                    <span>"{data.feedbackTheme1}"</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-slate-400 mr-3">•</span>
                    <span>"{data.feedbackTheme2}"</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-slate-400 mr-3">•</span>
                    <span>"{data.feedbackTheme3}"</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <Separator className="my-12" />

          {/* Strategic Takeaways */}
          <div className="mb-8">
            <h2 className="text-2xl font-light text-slate-900 mb-8">Strategic Takeaways</h2>
            <div className="space-y-4">
              <div className="flex items-start">
                <span className="text-slate-400 mr-4 mt-1">•</span>
                <p className="text-slate-700 leading-relaxed">
                  {data.strategicTakeaway1}
                </p>
              </div>
              <div className="flex items-start">
                <span className="text-slate-400 mr-4 mt-1">•</span>
                <p className="text-slate-700 leading-relaxed">
                  {data.strategicTakeaway2}
                </p>
              </div>
              <div className="flex items-start">
                <span className="text-slate-400 mr-4 mt-1">•</span>
                <p className="text-slate-700 leading-relaxed">
                  {data.strategicTakeaway3}
                </p>
              </div>
              <div className="flex items-start">
                <span className="text-slate-400 mr-4 mt-1">•</span>
                <p className="text-slate-700 leading-relaxed">
                  {data.strategicTakeaway4}
                </p>
              </div>
              <div className="flex items-start">
                <span className="text-slate-400 mr-4 mt-1">•</span>
                <p className="text-slate-700 leading-relaxed">
                  {data.strategicTakeaway5}
                </p>
              </div>
              <div className="flex items-start">
                <span className="text-slate-400 mr-4 mt-1">•</span>
                <p className="text-slate-700 leading-relaxed">
                  {data.strategicTakeaway6}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
